import React, { memo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
  Skeleton,
  TablePagination,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import type { DoctorShiftRule, DoctorShiftRuleGrouped } from '../../../../domain/doctorShiftRule';
import { formatTimeCondition, RULE_TYPE_LABELS } from '../../../../domain/doctorShiftRule';

interface DoctorShiftRuleTableProps {
  data: DoctorShiftRuleGrouped[];
  isLoading: boolean;
  meta: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
  } | null;
  onPageChange: (page: number) => void;
  onEdit: (rule: DoctorShiftRule, groupedRule: DoctorShiftRuleGrouped) => void;
  onDelete: (rule: DoctorShiftRule) => void;
}

/**
 * ルール表示用チップ
 */
const RuleChips: React.FC<{
  rules: DoctorShiftRule[];
  type: 'assignable' | 'prohibited';
  onEdit: (rule: DoctorShiftRule) => void;
  onDelete: (rule: DoctorShiftRule) => void;
}> = ({ rules, type, onEdit, onDelete }) => {
  if (rules.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  const color = type === 'assignable' ? 'success' : 'error';

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {rules.map((rule) => (
        <Box key={rule.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip
            title={
              <Box>
                <Typography variant="caption" component="div">
                  {RULE_TYPE_LABELS[type]}
                </Typography>
                {rule.timeConditions.map((tc, i) => (
                  <Typography key={i} variant="caption" component="div">
                    {formatTimeCondition(tc)}
                  </Typography>
                ))}
              </Box>
            }
          >
            <Chip
              label={rule.timeConditions.length > 0
                ? formatTimeCondition(rule.timeConditions[0])
                : '-'
              }
              size="small"
              color={color}
              variant="outlined"
            />
          </Tooltip>
          <IconButton
            aria-label="ルールを編集"
            size="small"
            onClick={() => onEdit(rule)}
            sx={{ p: 0.5 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="ルールを削除"
            size="small"
            onClick={() => onDelete(rule)}
            sx={{ p: 0.5 }}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};

/**
 * 医師シフトルールテーブルコンポーネント
 *
 * - テーブルカラム（医師コード、医師名、割り当て可能、割り当て禁止、編集、削除）
 * - ページネーション（デフォルト100件）
 * - ローディング時Skeleton表示
 */
const DoctorShiftRuleTable: React.FC<DoctorShiftRuleTableProps> = memo(({
  data,
  isLoading,
  meta,
  onPageChange,
  onEdit,
  onDelete
}) => {
  const handleChangePage = (_: unknown, newPage: number) => {
    onPageChange(newPage + 1);
  };

  // ローディング時のスケルトン表示
  if (isLoading && data.length === 0) {
    return (
      <TableContainer className="admin-rule-table" component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>医師コード</TableCell>
              <TableCell>医師名</TableCell>
              <TableCell>割り当て可能</TableCell>
              <TableCell>割り当て禁止</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(5)].map((_, index) => (
              <TableRow key={index}>
                <TableCell><Skeleton width={80} /></TableCell>
                <TableCell><Skeleton width={120} /></TableCell>
                <TableCell><Skeleton width={200} /></TableCell>
                <TableCell><Skeleton width={200} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // データなし
  if (!isLoading && data.length === 0) {
    return (
      <Paper className="admin-rule-empty" sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          シフトルールが登録されていません
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <TableContainer className="admin-rule-table" component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={120}>医師コード</TableCell>
              <TableCell width={150}>医師名</TableCell>
              <TableCell>割り当て可能</TableCell>
              <TableCell>割り当て禁止</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.doctorId} hover>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {row.doctorCode}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {row.doctorFullName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <RuleChips
                    rules={row.assignableRules}
                    type="assignable"
                    onEdit={(rule) => onEdit(rule, row)}
                    onDelete={onDelete}
                  />
                </TableCell>
                <TableCell>
                  <RuleChips
                    rules={row.prohibitedRules}
                    type="prohibited"
                    onEdit={(rule) => onEdit(rule, row)}
                    onDelete={onDelete}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ページネーション */}
      {meta && (
        <TablePagination
          component="div"
          count={meta.total}
          page={meta.currentPage - 1}
          rowsPerPage={meta.perPage}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[100]}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / ${count}件`
          }
        />
      )}
    </Box>
  );
});

DoctorShiftRuleTable.displayName = 'DoctorShiftRuleTable';

export default DoctorShiftRuleTable;
